
export interface TemplateSection { title: string; content: string; }
export interface DeliverableTemplate { id: string; name: string; description: string; sections: TemplateSection[]; }

export const TEMPLATES: DeliverableTemplate[] = [
  {
    id: 'executive_summary', name: 'Executive Summary', description: 'Concise overview for leadership',
    sections: [
      { title: 'Executive Summary', content: `[PROJECT_NAME] was initiated to address [BUSINESS_PROBLEM]. This deliverable presents [KEY_FINDINGS] and recommends [RECOMMENDATIONS].` },
      { title: 'Background', content: `[CLIENT_NAME] operates in [INDUSTRY] and faces [CHALLENGES]. Current state: [CURRENT_STATE]. Desired future state: [FUTURE_STATE].` },
      { title: 'Key Findings', content: `1. [Finding 1 with evidence]
2. [Finding 2 with evidence]
3. [Finding 3 with evidence]` },
      { title: 'Recommendations', content: `1. [Rec 1: Action + Expected Outcome + Timeline]
2. [Rec 2: Action + Expected Outcome + Timeline]
3. [Rec 3: Action + Expected Outcome + Timeline]` },
      { title: 'Next Steps', content: `| Step | Owner | Timeline |
|------|-------|----------|
| [Step 1] | [Owner] | [Date] |
| [Step 2] | [Owner] | [Date] |` }
    ]
  },
  {
    id: 'technical_spec', name: 'Technical Specification', description: 'Detailed technical requirements document',
    sections: [
      { title: 'Technical Specification', content: `This document specifies the technical requirements for [SYSTEM_NAME].` },
      { title: 'Architecture Overview', content: `[DIAGRAM_DESCRIPTION]. Components: [List]. Integration points: [List].` },
      { title: 'Functional Requirements', content: `| ID | Requirement | Priority | Status |
|----|-------------|----------|--------|
| FR-001 | [Req] | High | [Status] |` },
      { title: 'Non-Functional Requirements', content: `| ID | Category | Requirement | Target |
|----|----------|-------------|--------|
| NFR-001 | Performance | [Req] | [Target] |` },
      { title: 'Data Model', content: `[Entity descriptions and relationships]` },
      { title: 'API Specifications', content: `[Endpoint definitions]` },
      { title: 'Deployment Plan', content: `[Steps and environments]` }
    ]
  },
  {
    id: 'project_proposal', name: 'Project Proposal', description: 'Client-facing proposal document',
    sections: [
      { title: 'Project Proposal', content: `Prepared for: [CLIENT] | Date: [DATE] | Prepared by: [CONSULTANT]` },
      { title: 'Executive Summary', content: `We propose [SOLUTION] to address [PROBLEM]. Expected outcomes: [OUTCOMES]. Investment: $[AMOUNT]. Timeline: [TIMELINE].` },
      { title: 'Problem Statement', content: `[CLIENT] currently experiences [PAINS]. These issues result in [IMPACTS]. Root causes: [CAUSES].` },
      { title: 'Proposed Solution', content: `Our approach: [METHODOLOGY]. Key components: [LIST]. Differentiators: [LIST].` },
      { title: 'Implementation Plan', content: `Phase 1 ([Duration]): [Activities]
Phase 2 ([Duration]): [Activities]
Phase 3 ([Duration]): [Activities]` },
      { title: 'Investment', content: `Total project cost: $[AMOUNT]
Breakdown: [Categories]
Payment schedule: [Milestones]
ROI: [Calculation]` },
      { title: 'Team', content: `[Lead]: [Bio]
[Team members]: [Roles]
Relevant experience: [Projects]` },
      { title: 'Risk Mitigation', content: `| Risk | Probability | Impact | Mitigation |
|------|-------------|--------|------------|
| [Risk 1] | High | High | [Strategy] |` }
    ]
  },
  {
    id: 'status_update', name: 'Status Update', description: 'Project status report for stakeholders',
    sections: [
      { title: 'Status Update', content: `Project: [NAME] | Period: [DATE_RANGE] | Overall Status: [GREEN/YELLOW/RED]` },
      { title: 'Accomplishments', content: `✅ [Achievement 1]
✅ [Achievement 2]
✅ [Achievement 3]` },
      { title: 'Upcoming Work', content: `📋 [Task 1] — Due: [Date]
📋 [Task 2] — Due: [Date]
📋 [Task 3] — Due: [Date]` },
      { title: 'Risks & Issues', content: `⚠️ [Issue 1]: [Description] | Mitigation: [Action] | Owner: [Name]
⚠️ [Issue 2]: [Description] | Mitigation: [Action] | Owner: [Name]` },
      { title: 'Decisions Needed', content: `❓ [Decision 1]: [Context] | Recommend: [Option] | By: [Date]
❓ [Decision 2]: [Context] | Recommend: [Option] | By: [Date]` },
      { title: 'Budget', content: `Planned: $[X] | Actual: $[Y] | Variance: $[Z] ([P]%) | Forecast: $[W]` }
    ]
  },
  {
    id: 'research_report', name: 'Research Report', description: 'Structured research findings',
    sections: [
      { title: 'Research Report', content: `Topic: [TOPIC] | Date: [DATE] | Researcher: [NAME]` },
      { title: 'Executive Summary', content: `[2-3 sentence overview of key findings and recommendation]` },
      { title: 'Research Objectives', content: `1. [Objective 1]
2. [Objective 2]
3. [Objective 3]` },
      { title: 'Methodology', content: `[Research methods used: surveys, interviews, literature review, data analysis, etc.]` },
      { title: 'Key Findings', content: `### Finding 1: [Title]
[Description with supporting data]

### Finding 2: [Title]
[Description with supporting data]` },
      { title: 'Analysis', content: `[Interpretation of findings — what do they mean?]` },
      { title: 'Recommendations', content: `1. [Rec 1 — actionable, specific, with expected impact]
2. [Rec 2]
3. [Rec 3]` },
      { title: 'Appendix', content: `[Sources, raw data, methodology details]` }
    ]
  }
];

const SECTION_KEYWORDS: Record<string, string[]> = {
  'Executive Summary': ['summary', 'overview', 'high level', 'in conclusion', 'key takeaway'],
  'Background': ['background', 'context', 'currently', 'existing', 'as-is', 'today'],
  'Key Findings': ['found', 'discovered', 'analysis', 'results', 'data shows', 'we identified', 'revealed'],
  'Recommendations': ['recommend', 'suggest', 'should', 'propose', 'advise', 'we advise'],
  'Next Steps': ['next', 'step', 'action', 'follow up', 'going forward', 'moving forward'],
  'Problem Statement': ['problem', 'issue', 'challenge', 'pain point', 'difficulty', 'struggle'],
  'Proposed Solution': ['solution', 'approach', 'propose', 'recommend', 'methodology', 'strategy'],
  'Investment': ['cost', 'price', 'budget', 'investment', 'fee', 'pricing', '$'],
  'Risk': ['risk', 'concern', 'issue', 'mitigation', 'threat', 'blocker'],
  'Accomplishments': ['completed', 'done', 'achieved', 'delivered', 'shipped', 'finished'],
  'Upcoming Work': ['next', 'upcoming', 'planned', 'will', 'schedule', 'future'],
  'Methodology': ['method', 'approach', 'process', 'framework', 'technique', 'procedure']
};

export function mapContentToSections(rawContent: string, template: DeliverableTemplate): DeliverableTemplate {
  const sentences = rawContent.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 10);
  const mapped = { ...template, sections: template.sections.map(s => ({ ...s })) };

  mapped.sections.forEach(section => {
    const keywords = SECTION_KEYWORDS[section.title] || [];
    const relevant = sentences.filter(sentence => {
      const lower = sentence.toLowerCase();
      return keywords.some(kw => lower.includes(kw));
    });
    if (relevant.length > 0) {
      section.content = relevant.join('. ') + '.';
    }
  });

  return mapped;
}

export function fillPlaceholders(template: DeliverableTemplate, values: Record<string, string>): string {
  return template.sections.map(s => {
    let content = s.content;
    Object.entries(values).forEach(([key, val]) => {
      content = content.replace(new RegExp('\\[' + key + '\\]', 'g'), val || `[${key}]`);
    });
    return `# ${s.title}

${content}`;
  }).join('\n\n---\n\n');
}

export function exportAsMarkdown(template: DeliverableTemplate, values: Record<string, string>): string {
  const header = `---
title: "${values.PROJECT_NAME || template.name}"
author: "CatalystAgile"
date: "${new Date().toLocaleDateString()}"
template: "${template.name}"
---

`;
  return header + fillPlaceholders(template, values);
}

export function exportAsHTML(template: DeliverableTemplate, values: Record<string, string>): string {
  const md = fillPlaceholders(template, values);
  // Simple markdown-to-HTML conversion
  let html = md
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    .replace(/\n/gim, '<br>');
  return `<!DOCTYPE html><html><head><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;line-height:1.6;color:#333}</style></head><body>${html}</body></html>`;
}
